import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import {
  CallPROSolarmanDeviceDetailsResponse,
  CallPROSolarmanFirmwareDetailsResponse,
  CallPROSolarmanLastActionResponse,
  FieldList,
  SolarmanDeviceStatus,
} from 'ngx-modbus';
import { MatButtonModule } from '@angular/material/button';

import { SearchComponent } from '../components/search/search.component';
import {
  detectInverterPatternLabels,
  detectLoggerType,
  filterFirmwareByLoggerType,
  filterFirmwareBySerialPatterns,
  INVERTER_SERIAL_PATTERNS,
  SUGGESTED_FIRMWARE_TAGS,
} from '../firmware-metadata-editor/firmware-metadata.utils';
import { FirmwareWithMetadata } from '../firmware-metadata-editor/firmware-metadata.model';
import { FirmwareUpdateError } from '../../communication/upgrade-by-stick-logger.utils';

const getDisplayName = (fw: FirmwareWithMetadata): string => {
  if (fw.metadata?.reference) {
    return `${fw.metadata.reference} (${fw.name})`;
  }
  return fw.name;
};

const defaultLabels = {
  basicInfo: 'Basic info',
  serial: 'Serial',
  currentVersionInfo: 'Current Version info',
  moduleVersionNo: 'Module Version N°',
  lastUpgradeRecord: 'Last Upgrade Record',
  versionKeyName: 'Version Key Name',
  targetVersion: 'Target Version',
  upgradeTime: 'Upgrade Time',
  progress: 'Progress',
  status: 'Status',
  error: 'Error:',
  success: 'Success',
  terminated: 'Terminated',
  upgrading: 'Upgrading',
  currentUpgrading: 'Current Upgrading..',
  upgradeRequest: 'Upgrade Request',
  detectedLoggerType: 'Detected Logger Type',
  detectedModel: 'Detected Model',
  loggerOffline: 'Logger Offline',
  inverterOffline: 'Inverter Offline',
  unableToDetectLoggerType: 'Unable to detect logger type from module version',
  unableToDetectModel: 'Unable to detect model from inverter serial',
  selectedFirmware: 'Selected Firmware',
  name: 'Name:',
  version: 'Version:',
  releaseDate: 'Release Date:',
  notes: 'Notes:',
  tags: 'Tags',
  timeout: 'Timeout',
  updateVersion: 'Update Version',
  filterByTags: 'Filter by Tags',
  searchPlaceholder: 'Search firmware...',
  noFirmwareFound: 'No firmware found',
  tryAdjustingFilters: 'Try adjusting your search or filters',
};

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    MatButtonModule,
    SearchComponent,
  ],
  selector: 'lib-solarman-upgrade',
  templateUrl: './solarman-upgrade.component.html',
  styleUrls: ['./solarman-upgrade.component.scss'],
})
export class SolarmanUpgradeComponent {
  @Input() uiFramework: 'ionic' | 'material' = 'ionic';

  readonly _labels = { ...defaultLabels };
  @Input() set labels(value: Partial<typeof defaultLabels>) {
    Object.assign(this._labels, value);
  }

  private _deviceType: 'logger' | 'inverter' = 'logger';
  @Input() get deviceType(): 'logger' | 'inverter' {
    return this._deviceType;
  }
  set deviceType(value: 'logger' | 'inverter') {
    if (!value) {
      return;
    }

    const hasChanged = this._deviceType !== value;
    this._deviceType = value;

    if (hasChanged) {
      this.selectedTimeout = this.defaultTimeout;
      this.selectedVersion = null;
      this.applyFilters();
    }
  }

  @Input() isUpgrading?: boolean | null;
  @Input() lastAction?: CallPROSolarmanLastActionResponse | null;
  @Input() deviceDetails?: CallPROSolarmanDeviceDetailsResponse | null;
  @Input() serial?: string | null;
  @Input() moduleVersion?: string | FieldList[] | null;

  @Output() upgrade = new EventEmitter<{
    selectedVersion?: CallPROSolarmanFirmwareDetailsResponse | null;
    selectedTimeout?: number | null;
    deviceDetails?: CallPROSolarmanDeviceDetailsResponse | null;
  }>();

  // Legacy input (kept for backwards compatibility)
  private _fwList?: CallPROSolarmanFirmwareDetailsResponse[] | null;
  @Input() get fwList(): CallPROSolarmanFirmwareDetailsResponse[] | null | undefined {
    return this._fwList;
  }
  set fwList(value: CallPROSolarmanFirmwareDetailsResponse[] | null | undefined) {
    this._fwList = value;
    if (!this._fwListWithMetadata?.length) {
      this.applyFilters();
    }
  }

  // New input with metadata
  private _fwListWithMetadata?: FirmwareWithMetadata[] | null;
  @Input() get fwListWithMetadata(): FirmwareWithMetadata[] | null | undefined {
    return this._fwListWithMetadata;
  }
  set fwListWithMetadata(value: FirmwareWithMetadata[] | null | undefined) {
    this._fwListWithMetadata = value;
    this.applyFilters();
  }

  private readonly baseTags = SUGGESTED_FIRMWARE_TAGS;

  selectedVersion: FirmwareWithMetadata | null = null;
  selectedTimeout: number | null = 15;
  filteredVersionList?: FirmwareWithMetadata[] | null = null;
  availableTags: string[] = [];
  selectedFilterTags: string[] = [];
  searchQuery = '';

  readonly upgradeTimeout = [10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60];
  readonly FirmwareUpdateErrorDescription = FirmwareUpdateError;

  getDisplayName = getDisplayName;

  get defaultTimeout(): number {
    return this.deviceType === 'logger' ? 15 : 40;
  }

  get detectionLabel(): string {
    return this.deviceType === 'logger' ? this._labels.detectedLoggerType : this._labels.detectedModel;
  }

  get offlineLabel(): string {
    return this.deviceType === 'logger' ? this._labels.loggerOffline : this._labels.inverterOffline;
  }

  get unableToDetectLabel(): string {
    return this.deviceType === 'logger'
      ? this._labels.unableToDetectLoggerType
      : this._labels.unableToDetectModel;
  }

  /**
   * Detected type/model based on deviceType
   */
  get detectedType(): string | null {
    if (this.deviceType === 'logger') {
      return detectLoggerType((this.moduleVersion as string) || '');
    }
    // For inverter, return matchedModelFamily
    return this.matchedModelFamily;
  }

  /**
   * Matched inverter serial pattern labels (inverter only)
   */
  private get matchedPatternLabels(): string[] {
    return detectInverterPatternLabels(this.serial || '');
  }

  /**
   * Matched model family description for display (inverter only)
   */
  private get matchedModelFamily(): string | null {
    const labels = this.matchedPatternLabels;
    if (!labels.length) {
      return null;
    }
    const families = labels.map((label) => {
      const entry = INVERTER_SERIAL_PATTERNS.find((p) => p.label === label);
      return entry ? `${entry.label} — ${entry.modelFamily}` : label;
    });
    return families.join(', ');
  }

  /**
   * Firmware list filtered by device type compatibility
   */
  private get compatibleFwList(): FirmwareWithMetadata[] {
    if (this.deviceType === 'logger') {
      return filterFirmwareByLoggerType(this.sourceFwList, this.detectedType);
    }
    return filterFirmwareBySerialPatterns(this.sourceFwList, this.matchedPatternLabels);
  }

  /**
   * Whether firmware data has been loaded (either list)
   */
  get hasFirmwareData(): boolean {
    return !!(this._fwListWithMetadata?.length || this._fwList?.length);
  }

  get isConnected(): boolean {
    return [
      SolarmanDeviceStatus.Online,
      SolarmanDeviceStatus.Alarm,
    ].includes(this.deviceDetails?.deviceState || SolarmanDeviceStatus.Offline);
  }

  /**
   * Module version as FieldList[] (for inverter display)
   */
  get moduleVersionList(): FieldList[] | null {
    if (this.deviceType === 'inverter' && Array.isArray(this.moduleVersion)) {
      return this.moduleVersion;
    }
    return null;
  }

  /**
   * Module version as string (for logger display)
   */
  get moduleVersionString(): string | null {
    if (this.deviceType === 'logger' && typeof this.moduleVersion === 'string') {
      return this.moduleVersion;
    }
    return null;
  }

  private get sourceFwList(): FirmwareWithMetadata[] {
    if (this._fwListWithMetadata?.length) {
      return this._fwListWithMetadata;
    }
    return (this._fwList || []).map((fw) => ({
      id: fw.id,
      version: fw.version,
      name: fw.name,
      productId: '',
      firmwareType: this.deviceType,
    }));
  }

  onVersionQueryChange(newQuery: string) {
    this.searchQuery = newQuery;
    if (this.selectedVersion && newQuery !== this.selectedVersion.name) {
      this.selectedVersion = null;
    }
    this.applyFilters();
  }

  onVersionClearQuery() {
    this.selectedVersion = null;
    this.searchQuery = '';
    this.applyFilters();
  }

  selectVersionItem(item: FirmwareWithMetadata) {
    if (this.selectedVersion?.name === item.name) {
      this.selectedVersion = null;
    } else {
      this.selectedVersion = item;
    }
    if (this.selectedVersion) {
      this.searchQuery = this.selectedVersion.name;
    }
    this.applyFilters();
  }

  onTimeoutChange(timeout: number | null | undefined) {
    this.selectedTimeout = timeout || this.defaultTimeout;
  }

  getUpgradeProgress(progress: unknown): number {
    const numericProgress = Number(progress ?? 0);
    if (Number.isNaN(numericProgress)) {
      return 0;
    }
    return Math.max(0, Math.min(numericProgress, 100)) / 100;
  }

  toggleFilterTag(tag: string) {
    const index = this.selectedFilterTags.indexOf(tag);
    if (index >= 0) {
      this.selectedFilterTags.splice(index, 1);
    } else {
      this.selectedFilterTags.push(tag);
    }
    this.applyFilters();
  }

  isFilterTagActive(tag: string): boolean {
    return this.selectedFilterTags.includes(tag);
  }

  isCustomTag(tag: string): boolean {
    return !(this.baseTags as readonly string[]).includes(tag);
  }

  private applyFilters() {
    const compatible = this.compatibleFwList;
    this.availableTags = this.computeAvailableTags(compatible);
    let result = compatible;

    if (this.selectedFilterTags.length > 0) {
      result = result.filter((fw) => {
        const fwTags = fw.metadata?.tags || [];
        return this.selectedFilterTags.some((tag) => fwTags.includes(tag));
      });
    }

    const query = this.searchQuery.toLowerCase().trim();
    if (query) {
      result = result.filter((fw) => {
        const name = fw.name.toLowerCase();
        const version = fw.version.toLowerCase();
        const reference = fw.metadata?.reference?.toLowerCase() || '';
        return name.includes(query) || version.includes(query) || reference.includes(query);
      });
    }

    if (this.selectedFilterTags.length > 0) {
      result = result.sort((a, b) => {
        const aCount = (a.metadata?.tags || []).filter((t) => this.selectedFilterTags.includes(t)).length;
        const bCount = (b.metadata?.tags || []).filter((t) => this.selectedFilterTags.includes(t)).length;
        return bCount - aCount;
      });
    }

    this.filteredVersionList = result;
  }

  private computeAvailableTags(fwList: FirmwareWithMetadata[]): string[] {
    const allFwTags = new Set<string>();
    for (const fw of fwList) {
      if (fw.metadata?.tags) {
        for (const tag of fw.metadata.tags) {
          allFwTags.add(tag);
        }
      }
    }

    const matchedBaseTags = (this.baseTags as readonly string[]).filter((tag) => allFwTags.has(tag));
    const customTags = Array.from(allFwTags)
      .filter((tag) => !(this.baseTags as readonly string[]).includes(tag))
      .sort();

    return [...matchedBaseTags, ...customTags];
  }
}
